import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ReactPlayer from 'react-player/file';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShareIcon from '@mui/icons-material/Share';
import MoreVert from '@mui/icons-material/MoreVert';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Close from '@mui/icons-material/Close';
import { getPublicUrlFromFileKey } from '../../services/API/Api';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import COLORS from '../../Utils/colors';
import { BookmarkBorderRounded, Bookmark } from '@mui/icons-material';
import BottomModal from '../../Components/BottomModel';
import Comments from '../Post/Comments';
import CustomAlertDialog from '../../Components/CustomAlertDialog';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { PostUIStyles } from '../Post/PostUI.styles';
import { request } from '../../Utils/request';
import { deletePost } from '../../store/slices/PostSlice';
import { deleteShort } from '../../store/slices/ShortSlice';

const REPORT_REASONS = [
  "I just don't like it",
  'Bullying or unwanted contact',
  'Suicide, self-injury or eating disorders',
  'Violence, hate or exploitation',
  'Selling or promoting restricted items',
  'Nudity or sexual activity',
  'Scam, fraud or spam',
  'False information',
  'Intellectual property',
  'Others',
] as const;

interface ShortsCardProps {
  item: any;
  index: number;
  activeIndex: number;
  handleFollow: () => void;
  handleShare: () => void;
  videoRefs: React.RefObject<HTMLDivElement[]>;
  isPreview: boolean;
  currentUser: any;
  onLike?: (id: string, isLiked: boolean) => void;
  onBookmark?: (id: string, isSaved: boolean) => void;
  isMuted: boolean;
  onToggleMute: () => void;
}

export const ShortsCard: React.FC<ShortsCardProps> = ({
  item,
  index,
  activeIndex,
  handleFollow,
  handleShare,
  videoRefs,
  isPreview,
  currentUser,
  onLike,
  onBookmark,
  isMuted,
  onToggleMute
}) => {
  // Destructure all needed fields from item
  const {
    author = {},
    description = '',
    files = [],
    location = {},
    place = {},
    isLiked = false,
    likeCount = 0,
    isFollowing = false,
    _id = '',
    linkTo = null,
    commentCount = 0
  } = item || {};
  const user = author.username || author.name || '';
  // Prefer direct fileURL; fallback logic will generate a signed URL from fileKey if necessary
  const fileURL = files[0]?.fileURL || '';
  const fileKey = files[0]?.fileKey || '';
  const [videoUrl, setVideoUrl] = useState<string>(fileURL || '');
  // Track whether we've already attempted to fetch a signed URL to avoid repeated calls
  const hasTriedSignedRef = useRef(false);
  const playerRef = useRef<ReactPlayer>(null);
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  
  // Track video visibility and playing state
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Comment modal state
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  
  // Local state for optimistic updates
  const [localIsLiked, setLocalIsLiked] = useState(isLiked);
  const [localLikeCount, setLocalLikeCount] = useState(likeCount || 0);
  const [localCommentCount, setLocalCommentCount] = useState(commentCount ?? 0);
  const [localIsSaved, setLocalIsSaved] = useState(item?.isSaved || false);

  // Sync local comment count when item/commentCount from props changes
  useEffect(() => {
    setLocalCommentCount(item?.commentCount ?? 0);
  }, [item?._id, item?.commentCount]);

  // Navigation and authentication
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReportReason, setSelectedReportReason] = useState<string | null>(null);
  const [reportDescription, setReportDescription] = useState('');
  const [reportConfirmOpen, setReportConfirmOpen] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const deleteShortData = async (id: string) => {
    try {
      await request(`/posts/${id}`, {
        method: 'DELETE',
        authorization: true,
      });
      dispatch(deletePost(id));
      dispatch(deleteShort(id));
    } catch (err) {
      console.log('error deleting short', err);
    }
  };

  const openReportFlow = () => {
    handleMenuClose();
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setSelectedReportReason(null);
    setReportDescription('');
    setReportModalOpen(true);
  };

  const openReportConfirm = () => {
    if (!selectedReportReason) return;
    setReportModalOpen(false);
    setReportConfirmOpen(true);
  };

  const submitReport = async () => {
    if (!_id || !selectedReportReason) return;
    setReportSubmitting(true);
    try {
      await request(`/posts/${_id}/report`, {
        method: 'POST',
        authorization: true,
        body: {
          reason: selectedReportReason,
          description: selectedReportReason === 'Others' ? reportDescription : (reportDescription || undefined),
        },
      });
      setReportConfirmOpen(false);
      setSelectedReportReason(null);
      setReportDescription('');
    } catch (err) {
      console.log('error reporting short', err);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleReportListingClick = () => {
    if (reportSubmitting) return;
    submitReport();
  };

  // Constants for text truncation
  const MAX_DESCRIPTION_LENGTH = 50;
  const shouldShowSeeMore = description.length > MAX_DESCRIPTION_LENGTH;

  const handleToggleMute = () => {
    // Only allow mute toggle when this video is visible
    if (isVisible) {
      onToggleMute();
      setShowControls(true);
    }
  };

  const toggleDescription = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const handleCommentClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setCommentModalOpen(true);
  };

  const handleLikeClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // Optimistic update
    const newIsLiked = !localIsLiked;
    const newLikeCount = newIsLiked ? localLikeCount + 1 : localLikeCount - 1;
    
    setLocalIsLiked(newIsLiked);
    setLocalLikeCount(newLikeCount);
    
    // Call parent handler
    if (onLike) {
      onLike(_id, newIsLiked);
    }
  };

  const handleBookmarkClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // Optimistic update
    setLocalIsSaved(!localIsSaved);
    
    // Call parent handler
    if (onBookmark) {
      onBookmark(_id, !localIsSaved);
    }
  };

  // Auto-hide controls after 2 seconds
  useEffect(() => {
    if (showControls) {
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [showControls]);

  // Check video visibility and control playback using IntersectionObserver
  useEffect(() => {
    const videoElement = videoRefs.current[index];
    
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isIntersecting = entry.isIntersecting;
          const intersectionRatio = entry.intersectionRatio;
          
          // Video is visible if it's intersecting and at least 60% visible
          const shouldBeVisible = isIntersecting && intersectionRatio >= 0.6;
          
          if (shouldBeVisible !== isVisible) {
            console.log(`Video ${index}: visibility changed to ${shouldBeVisible} (ratio: ${intersectionRatio.toFixed(2)})`);
            setIsVisible(shouldBeVisible);
            
            if (shouldBeVisible) {
              console.log(`Starting video ${index}`);
              setIsPlaying(true);
            } else {
              console.log(`Stopping video ${index}`);
              setIsPlaying(false);
            }
          }
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: '0px'
      }
    );

    observer.observe(videoElement);
    
    return () => {
      observer.disconnect();
    };
  }, [index, isVisible, videoRefs]);


  // Initialize video source. If a direct URL exists, use it; otherwise try to generate a signed URL.
  useEffect(() => {
    const initializeVideo = async () => {
      setIsLoading(true);
      if (fileURL) {
        // Direct URL available – try this first
        setVideoUrl(fileURL);
        setIsLoading(false);
      } else if (fileKey && !isPreview) {
        // No direct URL – generate signed URL immediately
        try {
          const response = await getPublicUrlFromFileKey(fileKey, 'post');
          setVideoUrl(response?.data?.files[0]?.viewUrl);
          setIsLoading(false);
        } catch (error) {
          console.log('error fetching public url', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Neither URL nor key available – stop loading spinner
        setIsLoading(false);
      }
    };

    initializeVideo();
    // Reset signed-URL attempt flag whenever source data changes
    hasTriedSignedRef.current = false;
  }, [fileURL, fileKey, isPreview]);
  useEffect(() => {
    // Cleanup on unmount: blur the video element to prevent keyboard control
    const player = playerRef.current;
    return () => {
      // Stop the video when unmounting
      if (player) {
        player.getInternalPlayer()?.pause();
      }
      
      // Try to find the underlying video element and blur it
      const videoEl = document.querySelector('video');
      if (videoEl && document.activeElement === videoEl) {
        (videoEl as HTMLVideoElement).blur();
      }
    };
  }, []);

  // Cleanup when playing state changes to prevent conflicts
  useEffect(() => {
    const player = playerRef.current;
    return () => {
      // Ensure video is paused when playing state changes
      if (player) {
        player.getInternalPlayer()?.pause();
      }
    };
  }, [isPlaying]);
  return (
    <>
      <CustomAlertDialog
        open={openAlertDialog}
        title="Confirm Action"
        content="Are you sure you want to delete this short?"
        onClose={() => setOpenAlertDialog(false)}
        onAgree={() => {
          setOpenAlertDialog(false);
          deleteShortData(_id);
        }}
        agreeText="Yes"
        disagreeText="No"
      />
      <BottomModal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        title="Comments"
        children={<Comments postId={_id} onCommentAdded={() => setLocalCommentCount((c: number) => c + 1)} />}
      />
      <BottomModal
        open={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setSelectedReportReason(null);
          setReportDescription('');
        }}
        title="Report"
        maxHeight="70vh"
      >
        <Box sx={{ px: 0, pb: 2 }}>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2, px: 2 }}>
            Why are you reporting this short?
          </Typography>
          <List disablePadding>
            {REPORT_REASONS.map((reason) => (
              <ListItemButton
                key={reason}
                selected={selectedReportReason === reason}
                onClick={() => setSelectedReportReason(reason)}
                sx={{ py: 1.5 }}
              >
                <Typography variant="body2">{reason}</Typography>
              </ListItemButton>
            ))}
          </List>
          {selectedReportReason === 'Others' && (
            <Box sx={{ px: 2, pt: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Additional details (optional)"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                variant="outlined"
                size="small"
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, px: 2 }}>
            <Button variant="contained" disabled={!selectedReportReason} onClick={openReportConfirm}>
              Continue
            </Button>
          </Box>
        </Box>
      </BottomModal>
      <Dialog
        open={reportConfirmOpen}
        onClose={() => !reportSubmitting && setReportConfirmOpen(false)}
        aria-labelledby="report-confirm-title"
      >
        <DialogTitle id="report-confirm-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Confirm Report Submission</span>
          <IconButton size="small" onClick={() => !reportSubmitting && setReportConfirmOpen(false)} aria-label="close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <InfoOutlined color="action" sx={{ mt: 0.25 }} />
            <DialogContentText>
              You are about to report this short for review. If it violates our content policies, appropriate action will
              be taken. Your report remains confidential.
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="button"
            variant="contained"
            color="error"
            disabled={reportSubmitting}
            onClick={handleReportListingClick}
          >
            {reportSubmitting ? 'Submitting...' : 'Report listing'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          if (isVisible) {
            handleToggleMute();
          }
        }}
      ref={(el) => {
        if (el) {
          videoRefs.current[index] = el as HTMLDivElement;
        }
      }}
      sx={{
        height: 'calc(100vh - 56px)',
        scrollSnapAlign: 'start',
        position: 'relative',
        backgroundColor: 'black',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* Test with both ReactPlayer and HTML5 video */}
      <ReactPlayer
        key={`${index}-${isPlaying}`}
        ref={playerRef}
        url={videoUrl}
        playing={isPlaying}
        loop
        muted={isMuted}
        controls={false}
        width="100%"
        height="100%"
        style={{ objectFit: 'cover', pointerEvents: 'none' } as React.CSSProperties}
        volume={1}
        onReady={() => {
          setIsLoading(false);
          console.log(`Video ${index} ready`);
        }}
        onStart={() => {
          setIsLoading(false);
          console.log(`Video ${index} started playing`);
        }}
        onBuffer={() => {
          setIsLoading(true);
        }}
        onBufferEnd={() => {
          setIsLoading(false);
        }}
        onPlay={() => {
          console.log(`Video ${index} is now playing`);
        }}
        onPause={() => {
          console.log(`Video ${index} is now paused`);
        }}
        onEnded={() => {
          console.log(`Video ${index} ended`);
        }}
        onError={(e) => {
          setIsLoading(true);
          // If the direct URL fails, attempt to generate and use a signed URL
          (async () => {
            if (!hasTriedSignedRef.current && fileKey && !isPreview) {
              hasTriedSignedRef.current = true;
              try {
                const response = await getPublicUrlFromFileKey(fileKey, 'post');
                setVideoUrl(response?.data?.files[0]?.viewUrl);
                setIsLoading(false);
              } catch (error) {
                console.log('error fetching public url on player error', error);
                console.log('shorts onError 2', videoUrl);  
              }
            }
          })();
        }}
      />



      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {author?.username === currentUser?.username ? (
          <>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/post/input', { state: item });
              }}
            >
              <Typography>Edit</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setOpenAlertDialog(true);
              }}
            >
              <Typography>Delete</Typography>
            </MenuItem>
          </>
        ) : (
          <MenuItem onClick={openReportFlow}>
            <FlagOutlined sx={{ color: '#d32f2f', mr: 1, fontSize: 20 }} />
            <Typography sx={{ color: '#d32f2f' }}>Report listing</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Loader */}
      {isLoading && (
        <CircularProgress
          size={60}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'orange',
            zIndex: 12,
          }}
        />
      )}

      {/* Custom Mute/Unmute button */}
      {showControls && isVisible && (
        <IconButton
          onClick={handleToggleMute}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: 'white',
            zIndex: 10,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)',
            },
          }}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      )}

      <Box
        sx={{
          position: 'absolute',
          // if it is the last video, make it sticky to the bottom xs:70 sm:40
          bottom: { xs: 20, sm: 20 },
          left: 30,
          color: 'white',
          zIndex: 1,
          maxWidth: '80%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: 40,
          }}
        >
          <Avatar 
            src={author?.profilePhoto?.url || ''} 
            sx={{ 
              width: 32, 
              height: 32, 
              mr: 1, 
              cursor: 'pointer',
              zIndex: 16
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isLoggedIn) {
                navigate('/login');
                return;
              }
              navigate(`/profile/${author._id}`);
            }}
          />
          <Typography
            fontWeight="bold"
            fontSize="16px"
            color="white"
            sx={{ lineHeight: '32px', height: 32, display: 'flex', alignItems: 'center' }}
          >
            {user}
          </Typography>
          {!isPreview && author?.username !== currentUser?.username && (
            <Button
              variant="outlined"
              sx={{ 
                ml: 1.5, 
                height: 28, 
                minWidth: 60, 
                px: 1, 
                borderRadius: '6px', 
                color: 'white', 
                borderColor: 'white', 
                textTransform: 'none',
                zIndex: 15,
                position: 'relative'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFollow();
              }}
            >
              {isFollowing ? "Following" : "Follow"}
            </Button>
          )}
        </Box>
        {linkTo &&
                <Box
                onClick={() => {
                    if (!isLoggedIn) {
                        navigate('/login');
                        return;
                    }
                    if(linkTo?.module === "Trip"){
                        navigate(`/trips/${linkTo.id}?type=trip`);
                    }else{
                        navigate(`/trips/${linkTo.id}?type=itinerary`);
                    }
                }}
                // orientation="horizontal"
                sx={[PostUIStyles.bottomCardContent, { 
                    backgroundColor: '#F57C00', 
                    borderRadius: '6px', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '16px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    marginTop: '6px',
                    marginBottom: '6px',
                }]}
                >
                    <Typography sx={{ color: 'white' }}>
                        {linkTo?.module === "Trip" ? 'Book trip' : linkTo?.module === "Itinerary" ? "Buy itenary" : null}
                    </Typography>
                    <ArrowForwardIosIcon sx={{ color: 'white', fontSize: '14px' }} />
                </Box>
            }
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              lineHeight: 1.4,
              wordBreak: 'break-word'
            }}
          >
            {isDescriptionExpanded 
              ? description 
              : description.length > MAX_DESCRIPTION_LENGTH 
                ? `${description.substring(0, MAX_DESCRIPTION_LENGTH)}...` 
                : description
            }
          </Typography>
          {shouldShowSeeMore && (
            <Button
              onClick={toggleDescription}
              sx={{
                color: 'white',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                p: 0,
                minWidth: 'auto',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                }
              }}
            >
              {isDescriptionExpanded ? 'See less' : 'See more'}
            </Button>
          )}
        </Box>

      

        <Stack direction="row" sx={{ mt: 1 }}>
          <LocationOnIcon sx={{ fontSize: 'sm', color: '#4FC3F7' }} />
          <Typography sx={{ color: '#4FC3F7', fontSize: 'md', fontWeight: 'sm', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>{location?.name}</Typography>
        </Stack>

      
        <Stack 
          direction="row" 
          sx={{ 
            mt: 1, 
            mx: 0,
            cursor: 'pointer',
            zIndex: 16
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isLoggedIn) {
              navigate('/login');
              return;
            }
            navigate(`/places/${place?._id}`);
          }}
        >
          <MapOutlinedIcon sx={{ fontSize: 'sm', color: '#4FC3F7' }} />
          <Typography sx={{ color: '#4FC3F7', fontSize: 'md', fontWeight: 'sm' }}>{place?.name}</Typography>
        </Stack>
      </Box>

      <Stack
        spacing={2}
        sx={{
          position: 'absolute',
          right: 2,
          bottom: { xs: 100, sm: 40 },
          alignItems: 'center',
          zIndex: 15,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleLikeClick();
            }}
            sx={{ color: 'white', zIndex: 16 }}
          >
            {localIsLiked ? (
              <FavoriteIcon sx={{ fontSize: 30, color: 'red' }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: 30 }} />
            )}
          </IconButton>
          <Typography color="white">
            {localLikeCount ?? 0}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton onClick={(e) => {
            e.stopPropagation();
            handleCommentClick();
          }} sx={{ color: 'white', zIndex: 16 }} >
            <ChatBubbleOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
          <Typography color="white">
            {localCommentCount ?? 0}
          </Typography>
          {/* <Typography color="white" variant="caption">
              Comment
            </Typography> */}
        </Box>

        {/* Share */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton onClick={handleShare} sx={{ color: 'white', zIndex: 16 }}>
            <ShareIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleBookmarkClick();
            }}
            sx={{ color: 'white', zIndex: 16 }}
          >
            {localIsSaved ? (
              <Bookmark sx={{ color: COLORS.primary, fontSize: 30 }} />
            ) : (
              <BookmarkBorderRounded sx={{ fontSize: 30 }} />
            )}
          </IconButton>
        </Box>
        {/* More: Edit/Delete for own shorts, Report for others */}
        {!isPreview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <IconButton onClick={handleMenuClick} sx={{ color: 'white', zIndex: 16 }}>
              <MoreVert sx={{ fontSize: 30 }} />
            </IconButton>
          </Box>
        )}
      </Stack>
    </Box>
    </>
  );
};